import React, { useState, useRef, useEffect } from 'react';
import { Navbar, Container, Image, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import VendorLogoCard from './VendorLogoCard';
import VendorInfoCard from './VendorInfoCard';
import DispensaryInfoCard from './DispensaryInfoCard';
import SubscriptionWidget from './SubscriptionWidget';
import LogoutButton from './LogoutButton';
import './DashNavBar.css';

const DashNavBar = ({ vendorData }) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [showCopyAlert, setShowCopyAlert] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const sidebarRef = useRef(null);
  const accordionRefs = useRef({
    vendorInfo: null,
    vendorLogo: null,
    dispensaryInfo: null,
    subscription: null
  });
  
  const navigate = useNavigate();

  const handleClose = () => {
    setShowSidebar(false);
  };

  const handleLogoClick = () => {
    if (vendorData?._id) {
      navigate(`/vendor/${vendorData._id}/menu`);
    }
  };

  const handleCopyNumber = (e) => {
    // Stop event propagation to prevent sidebar from closing
    e.stopPropagation();
    
    navigator.clipboard.writeText('(888) 301-5480');
    setShowCopyAlert(true);
    setTimeout(() => setShowCopyAlert(false), 2000);
  };

  const toggleAccordion = (sectionId, e) => {
    // Stop event propagation to prevent sidebar from closing
    e.stopPropagation();
    
    setActiveAccordion(activeAccordion === sectionId ? null : sectionId);
  };

  // Close sidebar when clicking outside, but not when clicking on a modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a modal or modal backdrop
      const isModalClick = !!event.target.closest('.modal') || 
                           event.target.classList.contains('modal') ||
                           event.target.classList.contains('modal-backdrop') ||
                           event.target.classList.contains('modal-content');
      
      // Only close if clicking outside sidebar AND not on a modal element
      if (
        sidebarRef.current && 
        !sidebarRef.current.contains(event.target) && 
        !isModalClick &&
        showSidebar
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSidebar]);

  // Update accordion content heights
  useEffect(() => {
    Object.keys(accordionRefs.current).forEach(key => {
      const contentRef = accordionRefs.current[key];
      if (contentRef) {
        contentRef.style.maxHeight = 
          activeAccordion === key ? `${contentRef.scrollHeight}px` : '0px';
      }
    });
  }, [activeAccordion]);

  return (
    <>
      {showCopyAlert && (
        <Alert 
          variant="success" 
          className="copy-alert"
        >
          Phone number copied to clipboard!
        </Alert>
      )}
      
      <Navbar className="dashboard-navbar" variant="dark" fixed="top">
        <Container fluid>
          <Navbar.Brand>
            <Image
              src={require('../assets/images/miniLogo.jpg')}
              alt="Mini Logo"
              className="navbar-logo"
              onClick={handleLogoClick}
            />
          </Navbar.Brand>
          <div 
            className="dash-vendor-info-container"
            onClick={(e) => {
              e.stopPropagation();
              setShowSidebar(true);
            }}
          >
            <span className="dash-vendor-name">
              {vendorData?.firstName || 'Loading...'}
            </span>
            <Image
              src={vendorData?.logoUrl || ''}
              alt="Vendor Logo"
              className="dash-vendor-logo"
            />
          </div>
        </Container>
      </Navbar>

      <div 
        className={`sidebar-overlay ${showSidebar ? 'active' : ''}`} 
        onClick={(e) => {
          // Only close if not clicking on a modal
          if (!e.target.closest('.modal')) {
            handleClose();
          }
        }} 
      />

      <div 
        className={`sidebar ${showSidebar ? 'active' : ''}`} 
        ref={sidebarRef}
        onClick={(e) => e.stopPropagation()}
      >
        {showSidebar && (
          <>
            <div className="sidebar-header">
              <h5 className="sidebar-title">Profile Settings</h5>
              <button
                className="sidebar-close"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
                aria-label="Close sidebar"
              >
                ×
              </button>
            </div>

            <div className="sidebar-content">
              <div className="accordion">             
                {/* Text Notifications Accordion */}
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button 
                      className={`accordion-button ${activeAccordion === 'vendorInfo' ? 'active' : ''}`}
                      onClick={(e) => toggleAccordion('vendorInfo', e)}
                    >
                      Text Notifications
                    </button>
                  </h2>
                  <div 
                    className={`accordion-content ${activeAccordion === 'vendorInfo' ? 'active' : ''}`}
                    ref={el => accordionRefs.current.vendorInfo = el}
                  >
                    <div className="accordion-body">
                      <VendorInfoCard />
                      
                      <div className="notification-box mt-3">
                        <div className="notification-title">SMS Notifications Info</div>
                        <Image 
                          src={require('../assets/images/textNotice.jpg')}
                          alt="SMS Notification Example"
                          fluid
                          className="notification-image"
                        />
                        Order SMS notifications will be sent from 
                        <span className="phone-number">(888) 301-5480</span>
                        <button 
                          onClick={handleCopyNumber} 
                          className="copy-button"
                        >
                          Copy
                        </button>
                        <div className="notification-warning">
                          Weedhurry DOES NOT provide customer with any delivery updates or notifications after order is placed. Be sure to respond promptly to customers with delivery ETA by using 'Orders' tab once notification is received.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vendor Logo Accordion */}
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button 
                      className={`accordion-button ${activeAccordion === 'vendorLogo' ? 'active' : ''}`}
                      onClick={(e) => toggleAccordion('vendorLogo', e)}
                    >
                      Vendor Logo
                    </button>
                  </h2>
                  <div 
                    className={`accordion-content ${activeAccordion === 'vendorLogo' ? 'active' : ''}`}
                    ref={el => accordionRefs.current.vendorLogo = el}
                  >
                    <div className="accordion-body">
                      <VendorLogoCard />
                    </div>
                  </div>
                </div>

                {/* Dispensary Info Accordion */}
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button 
                      className={`accordion-button ${activeAccordion === 'dispensaryInfo' ? 'active' : ''}`}
                      onClick={(e) => toggleAccordion('dispensaryInfo', e)}
                    >
                      Dispensary Information
                    </button>
                  </h2>
                  <div 
                    className={`accordion-content ${activeAccordion === 'dispensaryInfo' ? 'active' : ''}`}
                    ref={el => accordionRefs.current.dispensaryInfo = el}
                  >
                    <div className="accordion-body">
                      <DispensaryInfoCard />
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-center mt-4 mb-4">
                <LogoutButton />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DashNavBar;